
// ThemeProvider.jsx
// ==============================================
// ThemeProvider.jsx - ENHANCED WITH RICH LIGHT THEMES
// Manages theme without unmounting the application.
// ==============================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authManager } from '@/managers/AuthManager';

const ThemeContext = createContext();

export const themes = {
  '1':  { name: 'Graphite Dark',      isDark: true,  color: '#888888', primaryRgb: '136, 136, 136' },
  '2':  { name: 'Smoked Jade',       isDark: true,  color: '#2FA97D', primaryRgb: '47, 169, 125' },
  '3':  { name: 'Azure Noir',         isDark: true,  color: '#3B82F6', primaryRgb: '59, 130, 246' },
  '4':  { name: 'Halo Stone',         isDark: true,  color: '#56C2FF', primaryRgb: '86, 194, 255' },
  '8':  { name: 'Vivid Orange Dark',  isDark: true,  color: '#F97316', primaryRgb: '249, 115, 22' },
  '9':  { name: 'Azure Blue Dark',    isDark: true,  color: '#3B82F6', primaryRgb: '59, 130, 246' },
  '10': { name: 'Paper & Ink',        isDark: false, color: '#5C4AD6', primaryRgb: '92, 74, 214' },
  '11': { name: 'Arctic Haze',        isDark: false, color: '#635BFF', primaryRgb: '99, 91, 255' },
  '12': { name: 'Morning Fog',        isDark: false, color: '#158076', primaryRgb: '21, 128, 118' },
};

const DEFAULT_THEME = '8';
const LOCAL_STORAGE_KEY = 'shelbula-theme-preference';
const SESSION_STORAGE_KEY = 'shelbula-session-theme';

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Gets the initial theme synchronously to prevent flicker.
// Priority: Session > Local > Default
const getInitialTheme = () => {
  try {
    const sessionTheme = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (sessionTheme && themes[sessionTheme]) return sessionTheme;
    
    const localTheme = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (localTheme && themes[localTheme]) return localTheme;
  } catch (error) {
    console.warn("Could not access storage to get initial theme.", error);
  }
  return DEFAULT_THEME;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(getInitialTheme);
  const userUidRef = useRef(authManager.getCurrentUser()?.uid);
  const syncedUserThemeRef = useRef(null);

  const applyThemeSelection = useCallback((themeId, { persistLocal = false } = {}) => {
    if (!themes[themeId]) return;

    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, themeId);
      if (persistLocal) {
        localStorage.setItem(LOCAL_STORAGE_KEY, themeId);
      }
    } catch (error) {
      console.warn('Could not persist theme selection to storage.', error);
    }

    setCurrentTheme(prevTheme => prevTheme === themeId ? prevTheme : themeId);
  }, []);

  // Effect to apply the theme to the DOM
  useEffect(() => {
    const themeDetails = themes[currentTheme];
    if (themeDetails) {
      document.documentElement.setAttribute('data-theme', currentTheme);
      document.documentElement.style.colorScheme = themeDetails.isDark ? 'dark' : 'light';
    }
  }, [currentTheme]);

  // Effect to hydrate the theme from auth state once settings finish loading.
  useEffect(() => {
    const syncThemeFromState = (state) => {
      const nextUid = state.user?.uid ?? null;
      const userChanged = nextUid !== userUidRef.current;

      if (userChanged) {
        userUidRef.current = nextUid;
        syncedUserThemeRef.current = null;
      }

      if (!nextUid) {
        if (userChanged) {
          setCurrentTheme(getInitialTheme());
        }
        return;
      }

      const preferredTheme = state.settings?.zPreferences?.theme;
      if (
        state.settingsLoaded &&
        preferredTheme &&
        themes[preferredTheme] &&
        preferredTheme !== syncedUserThemeRef.current
      ) {
        syncedUserThemeRef.current = preferredTheme;
        applyThemeSelection(preferredTheme, { persistLocal: true });
      }
    };

    syncThemeFromState(authManager.getState());
    const unsubscribe = authManager.subscribe(syncThemeFromState);

    return () => unsubscribe();
  }, [applyThemeSelection]);

  const setPersistentTheme = useCallback(async (themeId) => {
    if (!themes[themeId]) return;

    syncedUserThemeRef.current = themeId;
    applyThemeSelection(themeId, { persistLocal: true });
    
    try {
      if (authManager.getCurrentUser()) {
        const preferences = await authManager.loadPreferences() || {};
        await authManager.savePreferences({ ...preferences, theme: themeId });
      }
    } catch (error) {
      console.error('Error saving theme preference:', error);
    }
  }, [applyThemeSelection]);

  const saveCurrentThemeAsPersistent = useCallback(() => {
    setPersistentTheme(currentTheme);
  }, [currentTheme, setPersistentTheme]);

  const toggleTheme = useCallback(() => {
    const themeKeys = Object.keys(themes);
    const currentIndex = themeKeys.indexOf(currentTheme);
    const nextIndex = (currentIndex + 1) % themeKeys.length;
    setPersistentTheme(themeKeys[nextIndex]);
  }, [currentTheme, setPersistentTheme]);

  const value = {
    currentTheme,
    setTheme: setPersistentTheme,
    setPersistentTheme,
    saveCurrentThemeAsPersistent,
    themeName: themes[currentTheme]?.name,
    themes,
    toggleTheme,
    isDarkTheme: !!themes[currentTheme]?.isDark,
    isLightTheme: !themes[currentTheme]?.isDark,
    isLoading: false // Kept for compatibility, but is no longer used for rendering logic
  };

  // ALWAYS render children. This prevents the app from ever unmounting.
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;